import { formatAsAt, formatDay } from './format.ts';

/**
 * WHAT THE REPORTS SCREEN SAYS ABOUT A RUN'S DATES — items 33 and 37.
 *
 * A SNAPSHOT is a report whose source has no day column — Games hosted. Its dates are recorded
 * and name the file, and filter nothing, so nothing here may say a run of one "covers" them: it is
 * the register as it stood at the moment it ran. The server says which reports are snapshots, from
 * the same lookup the PDF and the e-mail use, so the screen cannot disagree with either.
 *
 * AN EMPTY RUN still has a file — the CSV with its column headings and a PDF that says in words
 * there were no rows — so the screen says so and offers both. When the figures are the nightly
 * ones it adds that a day not yet counted is not in the file, because the nightly job counts a
 * gaming day at 00:20 the next morning and an empty run of those figures usually means nothing was
 * counted yet.
 *
 * Pure, so every sentence is tested in each state it can show (run-words.test.ts).
 */

const range = (window: { from: string; to: string }): string =>
  `${formatDay(window.from)} – ${formatDay(window.to)}`;

/** "A", "A and B", "A, B and C". */
const listOf = (names: readonly string[]): string =>
  names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/**
 * The line above the standing reports.
 *
 * @param snapshots the names of the snapshot reports among those listed below it
 * @param below how many reports are listed below it
 */
export function windowLine(
  window: { from: string; to: string },
  snapshots: readonly string[],
  below: number,
): string {
  if (snapshots.length === 0) return `Every run below covers ${range(window)}.`;
  const one = snapshots.length === 1;
  /* Every report below is a snapshot: no run below covers the dates, so none is said to. */
  if (snapshots.length >= below) {
    return one
      ? `${listOf(snapshots)} is a snapshot as at the moment it runs — the dates name its file and do not filter it.`
      : `${listOf(snapshots)} are snapshots as at the moment they run — the dates name their files and do not filter them.`;
  }
  return one
    ? `Every run below covers ${range(window)}, except ${listOf(snapshots)}: a snapshot as at the moment it runs — the dates name its file and do not filter it.`
    : `Every run below covers ${range(window)}, except ${listOf(snapshots)}: snapshots as at the moment they run — the dates name their files and do not filter them.`;
}

const THE_FILE_STILL_EXISTS =
  'The file still exists — the CSV has the column headings and the PDF says in words that there were no rows.';

/** What the result of a run that matched nothing says. */
export function emptyRunWords(run: { snapshot: boolean; nightly: boolean }): {
  title: string;
  body: string;
} {
  if (run.snapshot) {
    return {
      title: 'No rows at that moment',
      body: `Nothing matched under your brand scope as at the moment it ran. ${THE_FILE_STILL_EXISTS}`,
    };
  }
  return {
    title: 'No rows in this window',
    body:
      `Nothing matched these dates under your brand scope. ${THE_FILE_STILL_EXISTS}` +
      (run.nightly
        ? ' Figures for a gaming day are counted at 00:20 the next morning; a day not yet counted is not in this file.'
        : ''),
  };
}

/** What a run cut at the row limit is told to do. A snapshot's dates cannot shorten it. */
export function cutShortAdvice(snapshot: boolean): string {
  return snapshot
    ? 'Its dates do not filter it: choose one brand or drop a dimension and run it again before using this for anything that has to balance.'
    : 'Narrow the date range or drop a dimension and run it again before using this for anything that has to balance.';
}

/**
 * The window a run-history row shows under its name: the dates the run was asked for, which the
 * server reads in the clock each window was drawn in. One day is said once — a scheduled run of
 * the nightly figures is its one gaming day.
 *
 * A snapshot is shown by the moment it was taken — the instant its PDF says "As at", its
 * `finishedAt` — and only when it produced a file: a failed or unfinished run took nothing, so it
 * claims no moment.
 */
export function historyWindow(row: {
  snapshot: boolean;
  status: string;
  rangeFrom: string;
  rangeTo: string;
  startedAt: string;
  finishedAt: string | null;
}): string {
  if (!row.snapshot) {
    return row.rangeFrom === row.rangeTo
      ? formatDay(row.rangeFrom)
      : `${formatDay(row.rangeFrom)} – ${formatDay(row.rangeTo)}`;
  }
  return row.status === 'SUCCEEDED'
    ? `As at ${formatAsAt(row.finishedAt ?? row.startedAt)}`
    : 'a snapshot — its dates do not filter it';
}

/** The ad-hoc builder's line under its choices. */
export function builderWindowLine(
  window: { from: string; to: string },
  snapshot: boolean,
  rowCap: number,
): string {
  return snapshot
    ? `A snapshot as at the moment it runs — the dates name the file and do not filter it. Capped at ${rowCap.toLocaleString()} rows.`
    : `Covers ${range(window)}, capped at ${rowCap.toLocaleString()} rows. Change the window on the reports screen behind this dialog.`;
}
